// src/pages/RiskManager.jsx — Trading Command Center
import React, { useState, useEffect } from 'react';
import { AlertOctagon, BarChart2, Brain, CheckCircle, XCircle, TrendingUp, ChevronDown, ChevronUp, MapPin, MessageCircle, Minus } from 'lucide-react';
import { getTodayTrades } from '../utils/stats';
import { format } from 'date-fns';

const BIASES = ['', 'Bullish', 'Bearish', 'Neutral', 'Wait & See'];
const STATES = ['', 'Focused', 'Calm', 'Tired', 'Distracted', 'Emotional', 'Confident', 'Anxious'];

const PRE_SESSION = [
  { id: 'reviewed',  label: 'I have reviewed the higher timeframe bias' },
  { id: 'news',      label: 'I have checked the economic calendar for news' },
  { id: 'levels',    label: 'I know my key levels for today' },
  { id: 'rested',    label: 'I am well rested and focused' },
  { id: 'rules',     label: 'I have reviewed my trading rules' },
  { id: 'limits',    label: 'I accept my daily loss limit and will respect it' },
];

const SESSIONS_KEY = 'odftrades_sessions';

function loadSessions() {
  try { return JSON.parse(localStorage.getItem(SESSIONS_KEY) || '{}'); } catch { return {}; }
}

function saveSession(dateKey, data) {
  try {
    const all = loadSessions();
    all[dateKey] = { ...all[dateKey], ...data, updatedAt: new Date().toISOString() };
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(all));
  } catch {}
}

export default function RiskManager({ trades, settings , lang = 'en'}) {
  const today       = format(new Date(), 'yyyy-MM-dd');
  const todayTrades = getTodayTrades(trades);
  const todayLosses = todayTrades.filter(t => t.result === 'Loss').length;
  const todayPnl    = todayTrades.reduce((s, t) => s + Number(t.pnl || 0), 0);
  const todayCount  = todayTrades.length;

  const maxTrades  = settings.maxTradesPerDay || 3;
  const maxLosses  = settings.maxDailyLosses  || 2;
  const maxDDLimit = settings.maxDailyLoss    || 300;

  const hitMaxTrades = todayCount  >= maxTrades;
  const hitMaxLosses = todayLosses >= maxLosses;
  const hitDailyLoss = todayPnl    <= -Math.abs(maxDDLimit);
  const stopTrading  = hitMaxTrades || hitMaxLosses || hitDailyLoss;

  // Load today's saved session on mount
  const [checklist,    setChecklist]   = useState({});
  const [bias,         setBias]        = useState('');
  const [keyLevels,    setKeyLevels]   = useState('');
  const [mentalState,  setMentalState] = useState('');
  const [sessionNote,  setSessionNote] = useState('');
  const [saved,        setSaved]       = useState(false);
  const [showHistory,  setShowHistory] = useState(false);
  const [history,      setHistory]     = useState({});

  useEffect(() => {
    const all     = loadSessions();
    const todayData = all[today] || {};
    setHistory(all);
    if (todayData.checklist)   setChecklist(todayData.checklist);
    if (todayData.bias)        setBias(todayData.bias);
    if (todayData.keyLevels)   setKeyLevels(todayData.keyLevels);
    if (todayData.mentalState) setMentalState(todayData.mentalState);
    if (todayData.sessionNote) setSessionNote(todayData.sessionNote);
  }, [today]);

  const allChecked     = PRE_SESSION.every(i => checklist[i.id]);
  const uncheckedCount = PRE_SESSION.filter(i => !checklist[i.id]).length;

  const toggleCheck = id => {
    const next = { ...checklist, [id]: !checklist[id] };
    setChecklist(next);
    saveSession(today, { checklist: next });
  };

  const handleBias = b => {
    setBias(b);
    saveSession(today, { bias: b });
  };

  const handleState = s => {
    setMentalState(s);
    saveSession(today, { mentalState: s });
  };

  const handleSave = () => {
    saveSession(today, { bias, keyLevels, mentalState, sessionNote, checklist });
    setHistory(loadSessions());
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const tradesLeft = Math.max(0, maxTrades  - todayCount);
  const lossesLeft = Math.max(0, maxLosses  - todayLosses);
  const pnlPct     = Math.min(100, (Math.abs(todayPnl) / maxDDLimit) * 100);

  const historyDates = Object.keys(history)
    .filter(d => d !== today)
    .sort((a, b) => b.localeCompare(a))
    .slice(0, 10);

  const biasColor = b =>
    b === 'Bullish' ? '#00e5a0' : b === 'Bearish' ? '#ff4d6d' : '#ffd166';

  return (
    <div className="fade-in" style={{ padding: '28px 32px', maxWidth: 1050, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, color: '#f0f0f5', margin: 0 }}>Trading Command Center</h1>
        <p style={{ color: '#55556a', fontSize: 12, margin: '3px 0 0', fontFamily: "'DM Mono', monospace" }}>
          {format(new Date(), 'EEEE, MMMM d yyyy')} · Pre-session checklist & live risk tracker
        </p>
      </div>

      {/* STOP banner */}
      {stopTrading && (
        <div style={{
          background: 'rgba(255,77,109,0.1)', border: '2px solid rgba(255,77,109,0.4)',
          borderRadius: 12, padding: '18px 22px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 14
        }}>
          <AlertOctagon size={28} style={{ color: '#ff4d6d', flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#ff4d6d', letterSpacing: '0.05em', fontFamily: "'DM Mono', monospace" }}>
              STOP TRADING TODAY
            </div>
            <div style={{ fontSize: 13, color: '#8888a8', marginTop: 3 }}>
              {hitDailyLoss && 'Daily loss limit reached. '}
              {hitMaxLosses && 'Max losses reached. '}
              {hitMaxTrades && 'Max trades reached. '}
              Protect your account. Come back tomorrow.
            </div>
          </div>
        </div>
      )}

      {/* Live Risk Tracker */}
      <div style={{ background: '#16161f', border: '1px solid #1e1e2e', borderRadius: 12, padding: 22, marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
          <BarChart2 size={15} style={{ color: '#4cc9f0' }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: '#f0f0f5', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'DM Mono', monospace" }}>
            Live Risk Tracker — Today
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: todayTrades.length > 0 ? 18 : 0 }}>
          {[
            { label: 'Trades Today',  value: `${todayCount} / ${maxTrades}`,  sub: hitMaxTrades ? 'Limit reached' : `${tradesLeft} remaining`,      warn: hitMaxTrades, ok: !hitMaxTrades, fill: Math.min(100, (todayCount / maxTrades) * 100) },
            { label: 'Losses Today',  value: `${todayLosses} / ${maxLosses}`, sub: hitMaxLosses ? 'Stop trading'  : `${lossesLeft} more allowed`,    warn: hitMaxLosses, ok: !hitMaxLosses, fill: Math.min(100, (todayLosses / maxLosses) * 100) },
            { label: "Today's P&L",   value: `${todayPnl >= 0 ? '+' : ''}$${todayPnl.toFixed(2)}`, sub: hitDailyLoss ? 'Daily limit hit' : `Limit: $${maxDDLimit}`, warn: hitDailyLoss, ok: todayPnl >= 0, fill: pnlPct },
          ].map((item, i) => (
            <div key={i} style={{
              background: item.warn ? 'rgba(255,77,109,0.08)' : item.ok ? 'rgba(0,229,160,0.04)' : 'rgba(255,209,102,0.04)',
              border: `1px solid ${item.warn ? 'rgba(255,77,109,0.25)' : item.ok ? 'rgba(0,229,160,0.15)' : 'rgba(255,209,102,0.15)'}`,
              borderRadius: 10, padding: '14px 16px'
            }}>
              <div style={{ fontSize: 10, color: '#55556a', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>{item.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'DM Mono', monospace", color: item.warn ? '#ff4d6d' : item.ok ? '#00e5a0' : '#ffd166', marginBottom: 6 }}>{item.value}</div>
              <div style={{ background: '#1e1e2e', borderRadius: 4, height: 4, marginBottom: 6, overflow: 'hidden' }}>
                <div style={{ width: `${item.fill}%`, height: '100%', background: item.warn ? '#ff4d6d' : item.ok ? '#00e5a0' : '#ffd166', borderRadius: 4, transition: 'width 0.5s ease' }} />
              </div>
              <div style={{ fontSize: 11, color: item.warn ? '#ff4d6d' : '#55556a' }}>{item.sub}</div>
            </div>
          ))}
        </div>
        {todayTrades.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 10, color: '#55556a', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Today's Trades</div>
            {todayTrades.map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: 'rgba(255,255,255,0.02)', borderRadius: 7, border: '1px solid #1e1e2e' }}>
                <span style={{ fontWeight: 600, color: '#f0f0f5', fontSize: 13, minWidth: 70 }}>{t.pair}</span>
                <span style={{ color: t.direction === 'Buy' ? '#00e5a0' : '#ff4d6d', fontSize: 12 }}>{t.direction}</span>
                <span style={{ color: Number(t.pnl) >= 0 ? '#00e5a0' : '#ff4d6d', fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 600 }}>
                  {Number(t.pnl) >= 0 ? '+' : ''}${Number(t.pnl).toFixed(2)}
                </span>
                <span style={{ marginLeft: 'auto', display:'flex', alignItems:'center' }}>
                  {t.result === 'Win'  ? <CheckCircle size={14} style={{ color:'#00e5a0' }} />
                 : t.result === 'Loss' ? <XCircle     size={14} style={{ color:'#ff4d6d' }} />
                 :                       <Minus       size={14} style={{ color:'#ffd166' }} />}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>
        {/* Pre-Session Checklist */}
        <div style={{ background: '#16161f', border: `1px solid ${allChecked ? 'rgba(0,229,160,0.25)' : '#1e1e2e'}`, borderRadius: 12, padding: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Brain size={15} style={{ color: '#ffd166' }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: '#f0f0f5', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'DM Mono', monospace" }}>
              Pre-Session Checklist
            </span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: allChecked ? '#00e5a0' : '#ffd166', fontFamily: "'DM Mono', monospace" }}>
              {PRE_SESSION.length - uncheckedCount}/{PRE_SESSION.length}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
            {PRE_SESSION.map(item => {
              const checked = checklist[item.id];
              return (
                <button key={item.id} type="button" onClick={() => toggleCheck(item.id)} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  background: checked ? 'rgba(0,229,160,0.06)' : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${checked ? 'rgba(0,229,160,0.2)' : '#1e1e2e'}`,
                  borderRadius: 7, padding: '9px 12px', cursor: 'pointer',
                  textAlign: 'left', width: '100%', outline: 'none', transition: 'all 0.15s'
                }}>
                  {checked
                    ? <CheckCircle size={14} style={{ color: '#00e5a0', flexShrink: 0 }} />
                    : <XCircle    size={14} style={{ color: '#2a2a3e', flexShrink: 0 }} />}
                  <span style={{ fontSize: 12, color: checked ? '#f0f0f5' : '#8888a8' }}>{item.label}</span>
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontSize: 10, color: '#55556a', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5 }}>Today's Bias</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {BIASES.filter(b => b).map(b => (
                  <button key={b} onClick={() => handleBias(b)} type="button" style={{
                    padding: '5px 12px', borderRadius: 6, border: '1px solid',
                    fontSize: 12, cursor: 'pointer', outline: 'none',
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    background: bias === b ? `${biasColor(b)}18` : 'transparent',
                    borderColor: bias === b ? biasColor(b) : '#1e1e2e',
                    color: bias === b ? biasColor(b) : '#8888a8',
                  }}>{b}</button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#55556a', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5 }}>Mental State</div>
              <select value={mentalState} onChange={e => handleState(e.target.value)} style={{
                background: '#0a0a0f', border: '1px solid #1e1e2e', borderRadius: 7,
                color: '#f0f0f5', padding: '8px 12px', fontSize: 13,
                fontFamily: "'IBM Plex Sans', sans-serif", outline: 'none', width: '100%'
              }}>
                {STATES.map(s => <option key={s} value={s}>{s || '— select —'}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#55556a', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5 }}>Key Levels / Notes</div>
              <textarea value={keyLevels} onChange={e => setKeyLevels(e.target.value)}
                placeholder="e.g. XAU resistance 2340, GBP support 1.2650..."
                style={{
                  background: '#0a0a0f', border: '1px solid #1e1e2e', borderRadius: 7,
                  color: '#f0f0f5', padding: '9px 12px', fontSize: 12,
                  fontFamily: "'IBM Plex Sans', sans-serif", outline: 'none',
                  width: '100%', resize: 'vertical', minHeight: 70, boxSizing: 'border-box'
                }}
              />
            </div>
            <button onClick={handleSave} type="button" style={{
              background: '#00e5a0', color: '#0a0a0f', border: 'none',
              borderRadius: 8, padding: '10px', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', fontFamily: "'IBM Plex Sans', sans-serif", width: '100%'
            }}>
              {saved ? '✓ Session Saved' : 'Save Pre-Session Notes'}
            </button>
            {saved && (
              <div style={{ fontSize: 11, color: '#00e5a0', textAlign: 'center', fontFamily: "'DM Mono', monospace" }}>
                Saved for {today} ✓
              </div>
            )}
          </div>
        </div>

        {/* Session Review + Risk Rules */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ background: '#16161f', border: '1px solid #1e1e2e', borderRadius: 12, padding: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <TrendingUp size={15} style={{ color: '#00e5a0' }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: '#f0f0f5', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'DM Mono', monospace" }}>
                Session Review
              </span>
            </div>
            <textarea value={sessionNote} onChange={e => setSessionNote(e.target.value)}
              placeholder="How did the session go? What did you do well? What would you change?"
              style={{
                background: '#0a0a0f', border: '1px solid #1e1e2e', borderRadius: 7,
                color: '#f0f0f5', padding: '10px 12px', fontSize: 12,
                fontFamily: "'IBM Plex Sans', sans-serif", outline: 'none',
                width: '100%', resize: 'vertical', minHeight: 100, boxSizing: 'border-box'
              }}
            />
            <button onClick={handleSave} type="button" style={{
              background: 'transparent', color: '#00e5a0', border: '1px solid rgba(0,229,160,0.25)',
              borderRadius: 8, padding: '8px', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: "'IBM Plex Sans', sans-serif", width: '100%', marginTop: 10
            }}>
              {saved ? '✓ Saved' : 'Save Review'}
            </button>
          </div>

          <div style={{ background: '#16161f', border: '1px solid #1e1e2e', borderRadius: 12, padding: 22 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#f0f0f5', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'DM Mono', monospace" }}>
              Active Risk Rules
            </div>
            {[
              ['Max Trades / Day', settings.maxTradesPerDay || 3],
              ['Max Losses / Day', settings.maxDailyLosses  || 2],
              ['Daily Loss Limit', `$${settings.maxDailyLoss || 300}`],
              ['Risk Per Trade',   `$${settings.riskPerTrade || 100}`],
            ].map(([label, val]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #1a1a26' }}>
                <span style={{ fontSize: 13, color: '#8888a8' }}>{label}</span>
                <span style={{ fontSize: 13, color: '#f0f0f5', fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>{val}</span>
              </div>
            ))}
            <div style={{ fontSize: 11, color: '#55556a', marginTop: 10 }}>Change in Settings → Risk Rules</div>
          </div>
        </div>
      </div>

      {/* Session History */}
      {historyDates.length > 0 && (
        <div style={{ background: '#16161f', border: '1px solid #1e1e2e', borderRadius: 12, overflow: 'hidden' }}>
          <button onClick={() => setShowHistory(h => !h)} type="button" style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 22px', background: 'none', border: 'none', cursor: 'pointer', outline: 'none'
          }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#f0f0f5', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'DM Mono', monospace" }}>
              Session History ({historyDates.length} days)
            </span>
            {showHistory ? <ChevronUp size={16} style={{ color: '#55556a' }} /> : <ChevronDown size={16} style={{ color: '#55556a' }} />}
          </button>
          {showHistory && (
            <div style={{ borderTop: '1px solid #1e1e2e' }}>
              {historyDates.map(date => {
                const d = history[date] || {};
                return (
                  <div key={date} style={{ padding: '14px 22px', borderBottom: '1px solid #1a1a26' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: d.keyLevels || d.sessionNote ? 8 : 0, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, color: '#55556a', fontFamily: "'DM Mono', monospace", minWidth: 90 }}>{date}</span>
                      {d.bias && (
                        <span style={{ fontSize: 11, color: biasColor(d.bias), background: `${biasColor(d.bias)}15`, padding: '2px 8px', borderRadius: 5, fontFamily: "'DM Mono', monospace" }}>
                          {d.bias}
                        </span>
                      )}
                      {d.mentalState && (
                        <span style={{ fontSize: 11, color: '#8888a8', background: 'rgba(255,255,255,0.04)', padding: '2px 8px', borderRadius: 5 }}>
                          {d.mentalState}
                        </span>
                      )}
                      {d.checklist && (
                        <span style={{ fontSize: 11, color: Object.values(d.checklist).every(Boolean) ? '#00e5a0' : '#ffd166', fontFamily: "'DM Mono', monospace" }}>
                          {Object.values(d.checklist).filter(Boolean).length}/{PRE_SESSION.length} checked
                        </span>
                      )}
                    </div>
                    {d.keyLevels && (
                      <div style={{ fontSize: 12, color: '#8888a8', marginBottom: 4, display:'flex', alignItems:'flex-start', gap:6 }}>
                        <MapPin size={12} style={{ color:'#55556a', marginTop:2, flexShrink:0 }} />
                        <span>{d.keyLevels}</span>
                      </div>
                    )}
                    {d.sessionNote && (
                      <div style={{ fontSize: 12, color: '#8888a8', fontStyle: 'italic', display:'flex', alignItems:'flex-start', gap:6 }}>
                        <MessageCircle size={12} style={{ color:'#55556a', marginTop:2, flexShrink:0 }} />
                        <span>{d.sessionNote}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
